import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import ApiClient from '../../utils/apiClient';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  subscriptionInfo: {
    product_name: string;
    access_level: string;
    end_date?: string;
  } | null;
}

export default function CancelSubscriptionDialog({ open, onClose, onSuccess, subscriptionInfo }: CancelSubscriptionDialogProps) {
  const theme = useTheme();
  const [cancellingSubscription, setCancellingSubscription] = React.useState(false);

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const data = await ApiClient.post('api/payments/cancel-subscription');
      if (data.success) {
        // Call onSuccess callback if provided, otherwise reload page
        if (onSuccess) {
          onSuccess();
          onClose(); // Close the dialog after successful cancellation
        } else {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setCancellingSubscription(false);
    }
  };

  const handleClose = () => {
    if (!cancellingSubscription) {
      onClose();
    }
  };

  // Reset loading state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setCancellingSubscription(false);
    }
  }, [open]);

  // Format the end date for display
  const formatEndDate = (endDate: string) => {
    try {
      const date = new Date(endDate);
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return endDate;
    }
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="cancel-subscription-dialog-title"
      aria-describedby="cancel-subscription-dialog-description"
    >
      <DialogTitle id="cancel-subscription-dialog-title">
        Cancel subscription
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="cancel-subscription-dialog-description" sx={{ mb: 2 }}>
          {subscriptionInfo && (
            <>
              You have paid for a <strong>{subscriptionInfo.product_name}</strong> plan
              {subscriptionInfo.end_date && (
                <> through to <strong>{formatEndDate(subscriptionInfo.end_date)}</strong></>
              )}. If you cancel your subscription, it will remain active until this date, but will not renew afterwards.
            </>
          )}
        </DialogContentText>
        <DialogContentText>
            Are you sure you want to cancel your subscription?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={cancellingSubscription}
          sx={{ 
            color: theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          No
        </Button>
        <Button 
          onClick={handleCancelSubscription} 
          color="error" 
          variant="contained"
          disabled={cancellingSubscription}
        >
          {cancellingSubscription ? 'Cancelling...' : 'Yes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
